import React, { useState, useEffect, } from 'react';
import { useLocation, } from "react-router-dom";

import MenuItem from '@material-ui/core/MenuItem';
import Box from '@material-ui/core/Box';
import Tooltip from '@material-ui/core/Tooltip';
import { ThemeProvider, makeStyles } from "@material-ui/core/styles";
import Drawer from '@material-ui/core/Drawer';
import IconButton from '@material-ui/core/IconButton';
import GetAppIcon from '@material-ui/icons/GetApp';
import LayersIcon from '@material-ui/icons/Layers';
import ListIcon from '@material-ui/icons/List';
import Menu from '@material-ui/core/Menu';

import moment from 'moment';

import { theme } from './styles/theme'
import Map from './Component/Map';
import Legends from './Component/Legends';
import Legend from './Component/Legend';
import FloodPanel from './Component/FloodPanel';
import ControlPanel from './Component/ControlPanel';
import ShortcutButton from './Component/ShortcutButton';
import { FORECASTS as FLOOD_FORECASTS, RISKS as FLOOD_RISKS } from './mapLayers/Flood'
import { calcOpacity, datetimeTitle } from './utils'
import {
  mapboxLabelsLayer,
  boundariesLayer,
  floodLayer,
  sedimentDisasterHazardMapLayer,
  floodInundationHazardMapLayer,
  landslideLayer,
  inundationLayer,
  highResolutionNowcastLayer,
  mapboxTrafficsLayer,
  hillShadeMapLayer,
} from './layers'

if (process.env.REACT_APP_ENV_FILE !== 'production') {
  console.log(process.env.REACT_APP_ENV_FILE);
}


const DEFAULT_OPACITY = 75; // [%]

// デモ用サンプルデータの時刻
const DEMO_DATA = [
  // { datetime: new Date('2019-10-12T18:00:00Z'), comment: '台風19号（T1919） 東日本に被害' },
  // { datetime: new Date('2021-06-24T08:30:00Z'), comment: '北上市で土砂災害の危険度が高まる' },
  { datetime: new Date('2020-07-03T20:00:00Z'), comment: 'キキクル配色確認' },
  // {datetime:new Date('2021-07-06T20:00:00Z'), comment: '島根県 線状降水帯 緊急安全確保'},
  // {datetime:new Date('2021-07-07T23:30:00Z'),  comment: '広島県 緊急安全確保'},
];

// レイヤーの初期値。配列順に最前面→再後面
const initialMapLayers = [
  mapboxLabelsLayer,
  boundariesLayer,
  floodLayer,
  sedimentDisasterHazardMapLayer,
  floodInundationHazardMapLayer,
  landslideLayer,
  inundationLayer,
  highResolutionNowcastLayer,
  mapboxTrafficsLayer,
  hillShadeMapLayer,
];

// ショートカットボタンのレイヤー。排他で表示する。
const shortcutButtonLayers = {
  floodLayer: floodLayer,
  floodInundationHazardMapLayer: floodInundationHazardMapLayer,
  sedimentDisasterHazardMapLayer: sedimentDisasterHazardMapLayer,
  landslideLayer: landslideLayer,
  inundationLayer: inundationLayer,
  highResolutionNowcastLayer: highResolutionNowcastLayer,
}

// デフォルトの表示レイヤー
mapboxLabelsLayer.isShow = true;
boundariesLayer.isShow = true;
mapboxTrafficsLayer.isShow = true;
highResolutionNowcastLayer.isShow = true;
hillShadeMapLayer.isShow = true;

// 洪水浸水ハザードマップ要素
const initialFloodInundationHazardMapElements = [
  { title: '早期の立退き避難が必要な区域', element: floodInundationHazardMapLayer.layer.elements[0] },
  { title: '河岸侵食によって家屋倒壊等の危険がある区域', element: floodInundationHazardMapLayer.layer.elements[1] },
  { title: '氾濫流によって家屋倒壊等の危険がある区域', element: floodInundationHazardMapLayer.layer.elements[2] },
  { title: '浸水深', element: floodInundationHazardMapLayer.layer.elements[3] },
];

// 土砂災害ハザードマップ要素
const initialSedimentDisasterHazardMapElements = [
  { title: '急傾斜地の崩壊 警戒区域', element: sedimentDisasterHazardMapLayer.layer.elements[0] },
  { title: '土石流 警戒区域', element: sedimentDisasterHazardMapLayer.layer.elements[1] },
  { title: '地すべり 警戒区域', element: sedimentDisasterHazardMapLayer.layer.elements[2] },
  { title: '土石流 危険区域', element: sedimentDisasterHazardMapLayer.layer.elements[3] },
  { title: '急傾斜地崩壊 危険箇所', element: sedimentDisasterHazardMapLayer.layer.elements[4] },
  { title: '地すべり 危険箇所', element: sedimentDisasterHazardMapLayer.layer.elements[5] },
];

const useStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    width: '100vw',
  },
  mapPanel: {
    height: '100vh',
    width: `100vw`,
  },

  settings: {
    overflow: 'overlay',
    position: 'absolute',
    top: 0,
    right: 0,
    borderRadius: 5,
  },

  title: {
    overflow: 'overlay',
    position: 'absolute',
    top: 10,
    left: 10,
    paddingLeft: 10,
    paddingRight: 10,
    font: "18px/24px 'Hiragino Kaku Gothic ProN', 'YuGothic', 'Meiryo', 'sans-serif'",
    color: "lightgray",
  },
  titleDatetime: {
    display: 'inline-block',
    fontWeight: 'bold',
    paddingLeft: 20,
    paddingBottom: 10,
  },
  datetimeSelector: {
    overflow: 'overlay',
    position: 'absolute',
    top: 10,
    left: 10,
    padding: 10,
  },
  inputDatetime: {
    font: "20px/24px 'Hiragino Kaku Gothic ProN', 'YuGothic', 'Meiryo', 'sans-serif'",
    color: "lightgray",
    "&$inputFocused": {
      color: "orange"
    }
  },
}));

function App() {
  const classes = useStyles();
  const [drawerState, setDrawerState] = useState({
    layers: false,
    flood: false,
  });
  const [isOpenLegends, setOpenLegendsDialog] = useState(false);

  const [mapLayers, setMapLayers] = useState(initialMapLayers);
  const [shortcutButtonState, setButtonState] = useState({
    layers: {
      // ボタンの初期値はレイヤーの初期値に合わせる。
      floodLayer: floodLayer.isShow,
      landslideLayer: landslideLayer.isShow,
      inundationLayer: inundationLayer.isShow,
      highResolutionNowcastLayer: highResolutionNowcastLayer.isShow,
      floodInundationHazardMapLayer: floodInundationHazardMapLayer.isShow,
      sedimentDisasterHazardMapLayer: sedimentDisasterHazardMapLayer.isShow,
    },
    move: false,
  });
  const [map, setMap] = useState(null);
  const [center, setCenter] = useState([0, 0]);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [demoDatetime, setDemoDatetime] = useState(DEMO_DATA[0].datetime);
  const [demoDatetimeMenuAnchorEl, setDemoDatetimeMenuAnchorEl] = useState(null);
  const [floodInundationHazardMapElements, setFloodInundationHazardMapElements] = useState(initialFloodInundationHazardMapElements);
  const [sedimentDisasterHazardMapElements, setSedimentDisasterHazardMapElements] = useState(initialSedimentDisasterHazardMapElements);
  const [opacities, setOpacities] = useState({
    landslideLayer: 40,
    inundationLayer: 40,
    highResolutionNowcastLayer: DEFAULT_OPACITY,
    floodInundationHazardMap: DEFAULT_OPACITY,
    sedimentDisasterHazardMap: DEFAULT_OPACITY,
  });
  const [poi, setPoi] = useState(null);

  useEffect(() => {
    if (!map) {
      return;
    }

    // レイヤー、レイヤー要素のオーダーが変化したら mapbox にも反映する。
    map.updateLayerOrder(getLayerOrder());
  }, [mapLayers, floodInundationHazardMapElements, sedimentDisasterHazardMapElements]);

  useEffect(() => {
    if (!map) {
      return;
    }

    landslideLayer.layer.setPaintOption(['fill-opacity'], opacities.landslideLayer / 100.0);
  }, [opacities.landslideLayer]);

  useEffect(() => {
    if (!map) {
      return;
    }

    inundationLayer.layer.setPaintOption(['fill-opacity'], opacities.inundationLayer / 100.0);
  }, [opacities.inundationLayer]);

  useEffect(() => {
    if (!map) {
      return;
    }

    highResolutionNowcastLayer.layer.setPaintOption(['raster-opacity'], opacities.highResolutionNowcastLayer / 100.0);
  }, [opacities.highResolutionNowcastLayer]);

  useEffect(() => {
    if (!map) {
      return;
    }

    setButtonState({ ...shortcutButtonState, move: (poi ? true : false) });
  }, [poi]);

  useEffect(() => {
    if (!map) {
      return;
    }

    // デモデータの時刻をセットする。
    const setDatetime = async () => {
      const layers = [landslideLayer, inundationLayer, floodLayer, highResolutionNowcastLayer,];

      for (let i = 0; i < layers.length; i++) {
        const layer = layers[i];
        layer.layer.setDemoUrl(demoDatetime);
        if (layer.isShow) {
          map.remove(layer.layer);
          await map.add(layer.layer);
        }
      }

      map.updateLayerOrder(getLayerOrder());

      // コンポーネントを再レンダリングさせる。
      {
        const layers = [...mapLayers];
        setMapLayers(layers);
      }
    };
    setDatetime();
  }, [demoDatetime]);

  const useQuery = () => {
    return new URLSearchParams(useLocation().search);
  }

  const onSaveImage = () => {
    if (!map) {
      return;
    }

    const a = document.createElement('a');
    const canvas = map.mapbox.getCanvas();
    a.href = canvas.toDataURL('image/png');
    a.download = 'download.png';
    a.click();
  };

  const onLoadMap = (async (map) => {
    // デフォルトのレイヤーを追加する。
    await Promise.all(mapLayers
      .filter(x => x.isShow)
      .map(x => {
        return map.add(x.layer);
      }))
      .catch((error) => {
        console.error(error)
      })

    map.updateLayerOrder(getLayerOrder());

    setMap(map);
    setIsMapLoaded(true);
  });

  const onMoveEndMap = (async (center, zoom) => {
    setCenter(center);
  });

  const onChangeLayer = (async (i, e) => {
    if (!isMapLoaded) {
      return;
    }

    const layer = mapLayers[i];

    const showLayer = async (layer, isShow) => {
      if (isShow) {
        if (!map.isExist(layer.layer)) {
          await map.add(layer.layer);
        }
      } else {
        if (map.isExist(layer.layer)) {
          map.remove(layer.layer);
        }
      }
      layer.isShow = isShow;
    }

    const isShow = !layer.isShow;  // 今の状態を反転する。
    await showLayer(layer, isShow);

    // オフからオンの切り替えで、ショートカットボタンのレイヤーなら自分以外を非表示にする。
    if (isShow) {
      let isShortcutButtonLayer = false;
      for (const x in shortcutButtonLayers) {
        if (x === layer.key) {
          isShortcutButtonLayer = true;
          break;
        }
      }

      if (isShortcutButtonLayer) {
        for (const x in shortcutButtonLayers) {
          if (x !== layer.key) {
            await showLayer(shortcutButtonLayers[x], false);
          }
        }
      }
    }

    // コンポーネントを再レンダリングさせる。
    const layers = [...mapLayers];
    setMapLayers(layers);

    // ショートカットボタンの状態を更新する。
    setButtonState({ ...shortcutButtonState, layers: makeLayerShortcutButtonState() });
  });

  // レイヤーショートカットボタンのハンドラー
  const onClickLayerShortcutButton = (async (key) => {
    if (!isMapLoaded) {
      return;
    }

    const isShow = !shortcutButtonState.layers[key];  // 今の状態を反転する。

    const showLayer = async (layer, isShow) => {
      if (isShow) {
        if (!map.isExist(layer.layer)) {
          await map.add(layer.layer);
        }
      } else {
        if (map.isExist(layer.layer)) {
          map.remove(layer.layer);
        }
      }
      layer.isShow = isShow;
    }

    // 排他でオンオフ切り替え
    for (const k in shortcutButtonLayers) {
      // 対象レイヤーのオンオフ切り替え
      if (k === key) {
        const x = shortcutButtonLayers[k];
        await showLayer(x, isShow);

      } else {
        // 対象ではないグループでオンのものはオフにする。
        const x = shortcutButtonLayers[k];
        if (isShow && x.isShow) {
          await showLayer(x, false);
        }
      }
    }

    // コンポーネントを再レンダリングさせる。
    const layers = [...mapLayers];
    setMapLayers(layers);

    // ショートカットボタンの状態を更新する。
    setButtonState({ ...shortcutButtonState, layers: makeLayerShortcutButtonState() });
  });

  // ズームボタンのハンドラー
  const onClickZoomButton = (async (key) => {
    if (!isMapLoaded) {
      return;
    }

    const DELTA = 1.0;
    const oldZoom = map.mapbox.getZoom();
    let zoom = oldZoom;
    switch (key) {
      case 'in':
        zoom = (oldZoom < 17.0) ? oldZoom + DELTA : 18.0;
        break;

      case 'out':
        zoom = (1.0 < oldZoom) ? oldZoom - DELTA : 0.0;
        break;

      default:
        throw new Error('invalid zoom button type.');
    }

    map.mapbox.flyTo({ zoom: zoom });
  });


  // 移動ボタンのハンドラー
  const onClickMoveButton = (async (key) => {
    if (!isMapLoaded) {
      return;
    }

    switch (key) {
      case 'resetOrRegist':
        if (poi) {
          if (poi.marker) {
            poi.marker.remove();
          }
          setPoi(null);

        } else {
          const marker = map.createMarker({ draggable: true });

          // ドラッグで位置とズームレベルを保存する。
          marker.on('dragend', () => {
            setPoi({ marker: marker, zoom: map.mapbox.getZoom() });
          });

          marker.setLngLat(map.mapbox.getCenter());

          setPoi({ marker: marker, zoom: map.mapbox.getZoom() });
        }
        break;

      case 'jump':
        if (poi) {
          map.mapbox.flyTo({ zoom: poi.zoom, center: poi.marker.getLngLat() });
        }
        break;

      default:
        throw new Error('invalid move button type.');
    }
  });



  const onChangeFloodInundationHazardMapElement = ((i, isShow) => {
    if (!isMapLoaded) {
      return;
    }

    floodInundationHazardMapLayer.layer.showElement(floodInundationHazardMapElements[i].element.id, isShow);

    const elements = [...floodInundationHazardMapElements];
    setFloodInundationHazardMapElements(elements);
  });

  const onChangeSedimentDisasterHazardMapElement = ((i, isShow) => {
    if (!isMapLoaded) {
      return;
    }

    sedimentDisasterHazardMapLayer.layer.showElement(sedimentDisasterHazardMapElements[i].element.id, isShow);

    const elements = [...sedimentDisasterHazardMapElements];
    setSedimentDisasterHazardMapElements(elements);
  });

  const onDropLayer = ((e) => {
    const { removedIndex, addedIndex } = e;

    const layers = [...mapLayers];
    let itemToAdd;

    if (removedIndex !== null) {
      itemToAdd = layers.splice(removedIndex, 1)[0];
    }

    if (addedIndex !== null) {
      layers.splice(addedIndex, 0, itemToAdd);
    }

    setMapLayers(layers);
  });

  const onDropFloodInundationElement = ((e) => {
    const { removedIndex, addedIndex } = e;

    const elements = [...floodInundationHazardMapElements];
    let itemToAdd;

    if (removedIndex !== null) {
      itemToAdd = elements.splice(removedIndex, 1)[0];
    }

    if (addedIndex !== null) {
      elements.splice(addedIndex, 0, itemToAdd);
    }

    floodInundationHazardMapLayer.layer.elements = elements.map(x => x.element);

    setFloodInundationHazardMapElements(elements);
  });

  const onDropSedimentDisasterElement = ((e) => {
    const { removedIndex, addedIndex } = e;

    const elements = [...sedimentDisasterHazardMapElements];
    let itemToAdd;

    if (removedIndex !== null) {
      itemToAdd = elements.splice(removedIndex, 1)[0];
    }

    if (addedIndex !== null) {
      elements.splice(addedIndex, 0, itemToAdd);
    }

    sedimentDisasterHazardMapLayer.layer.elements = elements.map(x => x.element);

    setSedimentDisasterHazardMapElements(elements);
  });

  const onChangeLandslideLayerOpacity = ((event, newValue) => {
    setOpacities({ ...opacities, landslideLayer: newValue });
  });

  const onChangeInundationLayerOpacity = ((event, newValue) => {
    setOpacities({ ...opacities, inundationLayer: newValue });
  });

  const onChangeHighResolutionNowcastLayerOpacity = ((event, newValue) => {
    setOpacities({ ...opacities, highResolutionNowcastLayer: newValue });
  });

  const onChangeFloodInundationHazardMapOpacity = ((event, newValue) => {
    const opacity = newValue / 100.0;

    // 浸水深に限定して opacity を更新する。
    const options = floodInundationHazardMapLayer.layer.paintOptions;
    for (const key in options) {
      if (key !== 'flood-l2') {
        continue;
      }

      const option = options[key];
      if (option['fill-opacity'] != null) {
        option['fill-opacity'] = opacity;
      }
    }
    floodInundationHazardMapLayer.layer.paintOptions = options;

    setOpacities({ ...opacities, floodInundationHazardMap: newValue });
  });

  const onChangeSedimentDisasterHazardMapOpacity = ((event, newValue) => {

    // 共通して opacity を更新する。
    const opacity = newValue / 100.0;
    sedimentDisasterHazardMapLayer.layer.setPaintOption(['fill-opacity'], opacity)

    setOpacities({ ...opacities, sedimentDisasterHazardMap: newValue });
  });

  const getLayerOrder = (() => {
    const order = [];

    // 配列順に最前面→再後面
    mapLayers.forEach(x => {
      if (x.isShow) {
        Object.keys(x.layer.order).forEach((key) => {
          const id = x.layer.order[key];
          order.push(id);
        });
      }
    });

    return order;
  });

  const onSelectFloodRiskRiver = ((param) => {
    const pos = floodLayer.layer.getRiskRiverPosition(param.row.id, param.row.code);
    if (pos) {
      map.mapbox.setCenter(pos);
      map.popup(pos, `洪水害 危険度分布<ul><li>${param.row.name ? param.row.name : '（名称不明）'}：${FLOOD_RISKS[param.row.code]}</li></ul>`);
    }
  });

  const onSelectFloodForecastRiver = ((param) => {
    const pos = floodLayer.layer.getForecastRiverPosition(param.row.id, param.row.code);
    if (pos) {
      map.mapbox.setCenter(pos);
      map.popup(pos, `洪水害 危険度分布<ul><li>${param.row.name ? param.row.name : '（名称不明）'}：${FLOOD_FORECASTS[param.row.code]}</li></ul>`);
    }
  });

  const onDropLegend = ((id, position) => {
    const layers = [...mapLayers];
    layers.forEach(layer => {
      layer.legends.forEach(legend => {
        if (legend.id === id) {
          legend.position = position;
        }
      })
    })
    setMapLayers(layers);
  });

  const onCloseControlPanel = ((e) => {
    toggleDrawer('layers', false)(e);
  });

  const onCloseFloodPanel = ((e) => {
    toggleDrawer('flood', false)(e);
  });

  const toggleDrawer = (type, open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }

    setDrawerState({ ...drawerState, [type]: open });
  };

  // レイヤーショートカットボタンの状態を mapLayers オブジェクトから組み立てる。
  const makeLayerShortcutButtonState = () => {
    const state = {
      floodLayer: false,
      floodInundationHazardMapLayer: false,
      sedimentDisasterHazardMapLayer: false,
      landslideLayer: false,
      inundationLayer: false,
      highResolutionNowcastLayer: false,
    };

    mapLayers.forEach(x => {
      switch (x.key) {
        case 'floodLayer':
        case 'landslideLayer':
        case 'inundationLayer':
        case 'highResolutionNowcastLayer':
        case 'sedimentDisasterHazardMapLayer':
        case 'floodInundationHazardMapLayer':
          state[x.key] = x.isShow;
          break;

        default:
          break;
      }
    });

    return state;
  };


  // let legends = [];
  // mapLayers
  //   .filter(x => x.isShow)
  //   .forEach(x => {
  //     x.legends.forEach(legend => {
  //       legends.push(
  //         <Legend
  //           key={legend.id}
  //           id={legend.id}
  //           position={legend.position}
  //           unit={legend.unit}
  //           items={legend.items}
  //           onDropLegend={onDropLegend} />
  //       )
  //     })
  //   })

  let legends = [];
  mapLayers
    .filter(x => x.isShow)
    .forEach(x => {
      legends.push(...x.legends);
    });

  const controlPanelHandlers = {
    onDropLayer: onDropLayer,
    onChangeLayer: onChangeLayer,
    onChangeLandslideLayerOpacity: onChangeLandslideLayerOpacity,
    onChangeInundationLayerOpacity: onChangeInundationLayerOpacity,
    onChangeHighResolutionNowcastLayerOpacity: onChangeHighResolutionNowcastLayerOpacity,
    onChangeFloodInundationHazardMapOpacity: onChangeFloodInundationHazardMapOpacity,
    onChangeSedimentDisasterHazardMapOpacity: onChangeSedimentDisasterHazardMapOpacity,
    onDropFloodInundationElement: onDropFloodInundationElement,
    onDropSedimentDisasterElement: onDropSedimentDisasterElement,
    onChangeFloodInundationHazardMapElement: onChangeFloodInundationHazardMapElement,
    onChangeSedimentDisasterHazardMapElement: onChangeSedimentDisasterHazardMapElement,
    onClosePanel: onCloseControlPanel,
  };

  const shortcutButtonHandlers = {
    onClickLayer: onClickLayerShortcutButton,
    onClickZoom: onClickZoomButton,
    onClickMove: onClickMoveButton,
  };

  const onChangeDemoDatetime = ((datetime) => {
    setDemoDatetime(datetime);
  });

  const onCloseLegends = () => {
    setOpenLegendsDialog(false);
  };

  // URL のクエリ文字列からデモモードを判定する
  const query = useQuery();
  const isDemo = query.get('isDemo') === 'true' ? true : false;
  if (isDemo) {
    console.log('デモモード');
    mapLayers.forEach(x => {
      x.layer.isDemo = isDemo;
      if (isDemo) {
        if (x.layer.setDemoUrl) {
          x.layer.setDemoUrl(demoDatetime);
        }
      }
    });
  }

  return (
    <ThemeProvider theme={theme}>
      <Box className={classes.root}  >
        <Box className={classes.mapPanel} bgcolor="white" >
          <Map onLoad={onLoadMap} onMoveEnd={onMoveEndMap} />
        </Box>

        {isDemo
          ? <Title
            shortcutButtonState={shortcutButtonState}
            onClick={e => setDemoDatetimeMenuAnchorEl(e.currentTarget)} />
          : <Title
            shortcutButtonState={shortcutButtonState} />
        }

        {demoDatetimeMenuAnchorEl
          ? <DemoDatetimeMenu
            anchorEl={demoDatetimeMenuAnchorEl}
            setAnchorEl={setDemoDatetimeMenuAnchorEl}
            onChangeItem={onChangeDemoDatetime} />
          : null
        }

        <ShortcutButton
          handlers={shortcutButtonHandlers}
          buttonState={shortcutButtonState} />
      </Box>

      <Box className={classes.settings} bgcolor={'#272727'}>
        <Tooltip title="画像保存" arrow enterDelay={1000} >
          <IconButton aria-label="save image" color="primary" onClick={onSaveImage}>
            <GetAppIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="キキクル 洪水害 河川リスト" arrow enterDelay={1000} >
          <IconButton variant="flood list" color="primary" onClick={toggleDrawer('flood', true)}>
            <ListIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="レイヤー設定" arrow enterDelay={1000} >
          <IconButton variant="layers" color="primary" onClick={toggleDrawer('layers', true)}>
            <LayersIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Drawer variant={'persistent'} anchor={'right'} open={drawerState.layers} onClose={toggleDrawer('layers', false)} >
        <ControlPanel
          mapLayers={mapLayers}
          opacities={opacities}
          elements={{ floodInundationHazardMap: floodInundationHazardMapElements, sedimentDisasterHazardMap: sedimentDisasterHazardMapElements }}
          handlers={controlPanelHandlers} />
      </Drawer>

      <Drawer variant={'persistent'} anchor={'right'} open={drawerState.flood} onClose={toggleDrawer('flood', false)} >
        <FloodPanel
          map={map}
          center={center}
          floodLayer={floodLayer}
          onClose={onCloseFloodPanel}
          onSelectRisk={onSelectFloodRiskRiver}
          onSelectForecast={onSelectFloodForecastRiver} />
      </Drawer>

      <Legends legends={legends} onDropLegend={onDropLegend} />
    </ThemeProvider >
  );
}

// タイトル。ショートカットボタンと連動させる。
function Title(props) {
  const { shortcutButtonState, onClick } = props;
  const classes = useStyles();

  // 最初にオンになっているショートカットボタンのレイヤーでタイトルを組み立てる。
  let layer = null;
  for (const key in shortcutButtonState.layers) {
    if (!shortcutButtonState.layers[key]) {
      continue;
    }

    layer = shortcutButtonLayers[key];
    break;
  }

  return (
    <Box onClick={onClick} className={classes.title} bgcolor="#272727" >
      {
        (layer == null)
          ?
          <Box>
            <h1>ハザードマップ</h1>
          </Box>
          :
          <Box>
            <h1>{layer.title}</h1>
            {layer.layer.datetime ? <Box className={classes.titleDatetime}>{datetimeTitle(layer.layer.datetime)}</Box > : ''}
          </Box>
      }
    </Box >
  );
}

function DemoDatetimeMenu(props) {
  const { anchorEl, setAnchorEl, onChangeItem } = props;

  const onCloseMenu = () => {
    setAnchorEl(null);
  };

  const onClickItem = (x) => {
    setAnchorEl(null);
    onChangeItem(x)
  };

  return (
    <Menu
      id="demo-data-menu"
      anchorEl={anchorEl}
      keepMounted
      open={Boolean(anchorEl)}
      onClose={onCloseMenu}
    >
      {DEMO_DATA.map(x =>
      (<MenuItem
        key={x.datetime}
        value={x.datetime}
        onClick={e => onClickItem(x.datetime)}>
        {moment(x.datetime).format('YYYY-MM-DD HH:mm')} : {x.comment}
      </MenuItem>)
      )}
    </Menu>
  );
};

export default App;
